import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';
import { Conversation, Message } from 'src/api/v1-api/helpers';
import { useAuth } from 'src/utils/auth';
import useRequest from 'src/utils/hooks/useRequest';
import { useMarketplaceContext } from './marketplace';
import useSingleToast from 'src/utils/hooks/toast/useSingleToast';
import * as Sentry from '@sentry/react';

type MessagingContextProps = {
  conversations: Conversation[] | null;
  conversationsMessages: { [key: number]: Message[] };
  notificationsCount: number;
  refreshConversations: () => Promise<void>;
  fetchConversationMessages: (conversationId: number, showLoading?: boolean) => Promise<void>;
  addMessageToConversation: (conversationId: number, body: string) => Promise<void>;
  updateConversationReadStatus: (conversationId: number) => void;
  messagingLoading: boolean;
  startConversation: (
    marketplaceId: number,
    contextType: string,
    subjectObjectId: number,
    otherAccountId: number,
    contextTitle: string,
  ) => Promise<Conversation | null>;
};

export const MessagingContext = createContext<MessagingContextProps>({
  conversations: null,
  conversationsMessages: {},
  notificationsCount: 0,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  refreshConversations: () => Promise.resolve(),
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  fetchConversationMessages: () => Promise.resolve(),
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  addMessageToConversation: () => Promise.resolve(),
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  updateConversationReadStatus: () => {},
  messagingLoading: false,
  startConversation() {
    return Promise.resolve(null);
  },
});

interface MessagingProviderProps {
  children: React.ReactNode;
}

export const MessagingProvider = ({ children }: MessagingProviderProps) => {
  const { api } = useRequest();
  const [conversations, setConversations] = useState<Conversation[] | null>(null);
  const [conversationsMessages, setConversationsMessages] = useState<{ [key: number]: Message[] }>(
    {},
  );
  const [notificationsCount, setNotificationsCount] = useState<number>(0);
  const { marketplace } = useMarketplaceContext();
  const { user, isAuthenticated } = useAuth();
  const [messagingLoading, setMessagingLoading] = useState(false);
  const showToast = useSingleToast();

  const conversationsRef = useRef<Conversation[] | null>(null);
  const conversationsMessagesRef = useRef<{ [key: number]: Message[] }>({});
  const isAuthenticatedRef = useRef<boolean>(isAuthenticated);

  const currentAccountId =
    (user &&
      marketplace &&
      user.marketplace_accounts.find((account) => account.marketplace_id === marketplace.id)?.id) ||
    0;

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    conversationsMessagesRef.current = conversationsMessages;
  }, [conversationsMessages]);

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  const refreshConversations = useCallback(async () => {
    if (marketplace?.id && isAuthenticated) {
      try {
        const data = await api.marketplace.messaging.conversation.list(marketplace.id);
        // only update if this is the first load (currentConversations is null),
        // there are new conversations, or last_message_id is updated somewhere
        // (triggering a new notification)
        const currentConversations = conversationsRef.current;
        const hasNewData =
          currentConversations === null ||
          (currentConversations !== null && data.length > currentConversations.length) ||
          data.some(
            (conversation) =>
              conversation.last_message_id !==
              currentConversations?.find((c) => c.id === conversation.id)?.last_message_id,
          );

        if (hasNewData) {
          conversationsRef.current = data;
          setConversations(data);
        }
      } catch (error: any) {
        // Check if this is an authentication error
        const isAuthError = error?.isAuthError === true || error?.status === 401;
        if (isAuthError) {
          // Stop further processing for auth errors - let the auth context handle it
          return;
        }

        // Only set empty array if we don't have any existing conversations
        // This prevents clearing conversations on temporary network errors
        if (conversationsRef.current === null) {
          conversationsRef.current = [];
          setConversations([]);
        }
        Sentry.captureException(error, {
          tags: {
            component: 'MessagingProvider',
            feature: 'messaging',
            operation: 'refresh_conversations',
            marketplaceId: marketplace?.id,
          },
          extra: {
            errorMessage: error instanceof Error ? error.message : String(error),
            errorType: error instanceof TypeError ? 'TypeError' : typeof error,
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketplace?.id, isAuthenticated, api]);

  useEffect(() => {
    if (marketplace?.id && isAuthenticated) {
      (async () => {
        await refreshConversations();
      })();
    }
  }, [marketplace?.id, isAuthenticated, refreshConversations]);

  // TODO: This is a barebones approach that should be improved by using server sent events or a websocket.
  // Auto-refresh conversations every minute while user is on the site, up to 1 hour
  useEffect(() => {
    if (!marketplace?.id || !isAuthenticated || typeof window === 'undefined') {
      return;
    }

    let isPageVisible = true;
    let isRefreshing = false;
    let intervalId: NodeJS.Timeout | null = null;
    const startTime = Date.now();
    const STOP_REFRESHING_AFTER_MS = 60 * 60 * 1000; // 1 hour in milliseconds

    const stopRefreshing = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const refreshInterval = async () => {
      const elapsedTime = Date.now() - startTime;

      if (elapsedTime >= STOP_REFRESHING_AFTER_MS) {
        stopRefreshing();
        return;
      }

      // Use ref to check current auth state (prevents stale closure issues)
      // If user is no longer authenticated, stop the interval
      if (!isAuthenticatedRef.current) {
        stopRefreshing();
        return;
      }

      if (isPageVisible && !isRefreshing) {
        isRefreshing = true;
        try {
          await refreshConversations();
        } finally {
          isRefreshing = false;
        }
      }
    };

    intervalId = setInterval(refreshInterval, 60000); // 60 seconds

    // Handle page visibility changes
    const handleVisibilityChange = async () => {
      isPageVisible = !document.hidden;
      const elapsedTime = Date.now() - startTime;

      // Don't refresh if more than 1 hour has passed
      if (elapsedTime >= STOP_REFRESHING_AFTER_MS) {
        return;
      }

      // Use ref to check current auth state (prevents stale closure issues)
      // If user is no longer authenticated, stop the interval
      if (!isAuthenticatedRef.current) {
        stopRefreshing();
        return;
      }

      if (isPageVisible && !isRefreshing) {
        isRefreshing = true;
        try {
          // Refresh immediately when page becomes visible
          await refreshConversations();
        } finally {
          isRefreshing = false;
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopRefreshing();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [marketplace?.id, isAuthenticated, refreshConversations]);

  const updateNotificationsCount = useCallback(() => {
    const currentConversations = conversationsRef.current;
    if (!user?.id || currentConversations === null || currentConversations.length === 0) {
      return;
    }

    let count = 0;
    currentConversations.forEach((conversation) => {
      const userParticipant = conversation.participants.find(
        (participant) => participant.marketplace_account_id === currentAccountId,
      );
      if (userParticipant) {
        if (userParticipant.most_recent_message_id !== conversation.last_message_id) {
          count += 1;
        }
      }
    });
    setNotificationsCount(count);
  }, [user?.id, currentAccountId]);

  useEffect(() => {
    if (marketplace?.id && user?.id && conversations !== null && conversations.length > 0) {
      updateNotificationsCount();
    }
  }, [conversations, marketplace?.id, user?.id, updateNotificationsCount]);

  // TODO: add pagination
  const fetchConversationMessages = useCallback(
    async (conversationId: number, showLoading = true) => {
      if (isAuthenticated) {
        try {
          if (showLoading) {
            setMessagingLoading(true);
          }
          const data = await api.marketplace.messaging.message.get(conversationId);
          // merge new messages with existing ones to preserve pagination
          const currentMessages = conversationsMessagesRef.current;
          const existingMessages = currentMessages[conversationId] || [];
          const newMessages = data.results.filter(
            (message) => !existingMessages.some((m) => m.id === message.id),
          );

          // Always update the messages state, even if there are no new messages
          // This ensures the conversation is marked as loaded (not undefined)
          const updatedMessages = {
            ...currentMessages,
            [conversationId]: [...existingMessages, ...newMessages],
          };
          conversationsMessagesRef.current = updatedMessages;
          setConversationsMessages(updatedMessages);
        } catch (error) {
          // Even on error, set an empty array to prevent infinite loading
          const currentMessages = conversationsMessagesRef.current;
          const updatedMessages = {
            ...currentMessages,
            [conversationId]: currentMessages[conversationId] || [],
          };
          conversationsMessagesRef.current = updatedMessages;
          setConversationsMessages(updatedMessages);
          console.error('Error fetching conversation messages:', error);
          Sentry.captureException(error, {
            tags: {
              component: 'MessagingProvider',
              feature: 'messaging',
              operation: 'fetch_conversation_messages',
              marketplaceId: marketplace?.id,
            },
          });
        } finally {
          if (showLoading) {
            setMessagingLoading(false);
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAuthenticated, api],
  );

  const addMessageToConversation = useCallback(
    async (conversationId: number, body: string) => {
      if (isAuthenticated) {
        try {
          await api.marketplace.messaging.message.create(conversationId, { body });

          // Immediately fetch the conversation with the new message without triggering loading state
          // to prevent scroll jank in the thread view
          await fetchConversationMessages(conversationId, false);
        } catch (error) {
          // Let the UI handle user-initiated error
          throw error;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchConversationMessages, isAuthenticated, api],
  );

  const updateConversationReadStatus = useCallback(
    (conversationId: number) => {
      const currentConversations = conversationsRef.current;
      const conversation = currentConversations?.find((c) => c.id === conversationId);

      if (!conversation || !conversation.last_message_id) {
        return;
      }

      // update the current user's most recent message id immediately for UI update
      const updatedConversations = currentConversations?.map((conv) =>
        conv.id === conversationId
          ? {
              ...conv,
              participants: conv.participants.map((participant) =>
                participant.marketplace_account_id === currentAccountId
                  ? { ...participant, most_recent_message_id: conv.last_message_id }
                  : participant,
              ),
            }
          : conv,
      );

      if (updatedConversations) {
        conversationsRef.current = updatedConversations;
        setConversations(updatedConversations);
      }

      // update the read status in the backend
      if (isAuthenticated) {
        api.marketplace.messaging.message.read(conversationId, conversation.last_message_id);
      }
    },
    [currentAccountId, isAuthenticated, api],
  );

  const startConversation = useCallback(
    async (
      marketplaceId: number,
      contextType: string,
      subjectObjectId: number,
      otherAccountId: number,
      contextTitle: string,
    ) => {
      if (isAuthenticated) {
        try {
          const data = await api.marketplace.messaging.conversation.create(marketplaceId, {
            kind: contextType,
            title: `Re: ${contextTitle}`,
            subject_object_id: subjectObjectId,
            participant_account_ids: [otherAccountId],
          });
          await refreshConversations();
          return data;
        } catch (error) {
          // Extract error details for better debugging
          const errorMessage =
            error instanceof Error
              ? error.message
              : String(error || 'Failed to start conversation');

          showToast({
            title: 'Failed to start conversation',
            description: 'Unable to create a new conversation. Please try again.',
            status: 'error',
            duration: 5000,
            isClosable: true,
            sentryAlert: {
              error: error instanceof Error ? error : new Error(errorMessage),
              level: 'error',
              tags: {
                feature: 'messaging',
                operation: 'start_conversation',
              },
              extras: {
                errorDetails: error,
              },
            },
          });
          return null;
        }
      } else {
        return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refreshConversations, isAuthenticated, api],
  );

  const contextValue = useMemo(
    () => ({
      conversations,
      conversationsMessages,
      notificationsCount,
      refreshConversations,
      fetchConversationMessages,
      addMessageToConversation,
      updateConversationReadStatus,
      messagingLoading,
      startConversation,
    }),
    [
      conversations,
      conversationsMessages,
      notificationsCount,
      refreshConversations,
      fetchConversationMessages,
      addMessageToConversation,
      updateConversationReadStatus,
      messagingLoading,
      startConversation,
    ],
  );
  return <MessagingContext.Provider value={contextValue}>{children}</MessagingContext.Provider>;
};

export const useMessaging = () => useContext(MessagingContext);
